// Card renderer — displays the current card in the main area: tags,
// direction badge, prompt text, and resets the answer input/feedback.
// The session controller (next slice) calls render() after picking a
// new card. buildAgreementHtml() is exported for the feedback pane
// (consumed by session.ts when revealing the answer).

import { LANG_SHORT, type Lang } from "../config/langs";
import { DEFAULT_LEVEL } from "../config/defaults";
import { tts } from "../api/tts";
import { currentLocale, t } from "../i18n";
import { translateTag } from "../i18n/tag-i18n";
import { faceLang } from "../domain/direction";
import {
  AGREEMENT_LABELS,
  getPromptText,
  splitAgreements,
} from "../domain/text";
import type { CardList } from "../config/lists";
import type { Card, Direction } from "../types";
import { byId } from "./dom";
import { escapeHtml, ttsButtonHtml } from "./html";

/** Minimal surface the card renderer needs — avoids taking the whole
 *  god-object so this stays easy to swap in tests / Phase 3. */
export type CardRenderDeps = {
  getCurrent: () => Card | null;
  getDirection: () => Direction;
  /** The active deck — renderer reads its shape (to decide whether to
   *  show the CEFR level + pick arrow label style) and its faces (to
   *  resolve face-key → Lang for TTS / arrow label). */
  getList: () => CardList;
  /** Flip the session phase back to "answer" after a fresh render. */
  setPhase: (phase: "answer" | "reveal") => void;
  /** Re-render session counters + sync dot after the card changes. */
  renderStats: () => void;
  /** Per-deck TTS master toggle — when false, the ♪ prompt speaker is
   *  omitted entirely (same gate session.ts applies to feedback). */
  isTtsEnabled: () => boolean;
};

/** HTML fragment describing agreement forms for the target (and, if
 *  present, source) language. Returns "" when neither side has
 *  agreement info — safe to drop straight into innerHTML. */
export function buildAgreementHtml(card: Card, dir: Direction): string {
  const targetText = card.faces[dir.to] || "";
  const sourceText = card.faces[dir.from] || "";
  const targetInfo = splitAgreements(targetText);
  const sourceInfo = splitAgreements(sourceText);
  let html = "";
  const label = t("agreement_label") as string;
  if (targetInfo.hasAgreements) {
    const items = targetInfo.agreements
      .map((form, i) => `${escapeHtml(form)} (${AGREEMENT_LABELS[i] || ""})`)
      .join(" · ");
    html += `<div class="agreement-line"><span class="label">${label}</span> ${items}</div>`;
  }
  if (sourceInfo.hasAgreements) {
    const items = sourceInfo.agreements
      .map((form, i) => `${escapeHtml(form)} (${AGREEMENT_LABELS[i] || ""})`)
      .join(" · ");
    html += `<div class="agreement-line"><span class="label">${label}</span> ${items}</div>`;
  }
  return html;
}

export function renderCard(deps: CardRenderDeps): void {
  const c = deps.getCurrent();
  if (!c) return;
  const dir = deps.getDirection();
  const list = deps.getList();
  const locale = currentLocale();
  const fromLang = faceLang(dir.from, list, locale);
  const toLang = faceLang(dir.to, list, locale);
  // Stop any lingering utterance from the previous card — prevents the
  // previous answer from overlapping a fresh autoplay on mobile.
  tts.cancel();

  const cardTags = byId("cardTags");
  if (cardTags) {
    cardTags.innerHTML = c.tags
      .map((tag) => `<span class="tag">${escapeHtml(translateTag(tag, locale))}</span>`)
      .join("");
  }

  // Direction badge — bilingual uses lang short codes (FR → BG); mono
  // uses the face's own label (pays → capitale). Level badge is shown
  // only for bilingual decks since CEFR is language-specific.
  const lvl = c.level || DEFAULT_LEVEL;
  const fromFace = list.faces.find((f) => f.key === dir.from);
  const toFace = list.faces.find((f) => f.key === dir.to);
  const arrow =
    list.shape === "bilingual"
      ? `${LANG_SHORT[fromLang]} ${t("card_arrow")} ${LANG_SHORT[toLang]}`
      : `${escapeHtml(t(fromFace?.labelKey ?? "") as string)} ${t("card_arrow")} ${escapeHtml(t(toFace?.labelKey ?? "") as string)}`;
  const levelBadge =
    list.shape === "bilingual"
      ? `<span class="level-badge lvl-${lvl}">${lvl}</span>`
      : "";
  const cardDirection = byId("cardDirection");
  if (cardDirection) {
    cardDirection.innerHTML = `${levelBadge}<span>${arrow}</span>`;
  }

  const cardPrompt = byId("cardPrompt");
  if (cardPrompt) {
    const promptText = getPromptText(c, dir);
    // ttsButtonHtml returns "" when unsupported and emits a `hidden`
    // attribute when no voice matches the prompt's language yet — the
    // tts module un-hides the button once voiceschanged delivers one.
    cardPrompt.innerHTML =
      `<span class="prompt-text">${escapeHtml(promptText)}</span>` +
      (deps.isTtsEnabled()
        ? ttsButtonHtml(promptText, fromLang, t("tts_play") as string)
        : "");
    // Serif styling for cyrillic-heavy prompts — triggered on the
    // resolved Lang so mono decks in BG UI get the same font treatment.
    cardPrompt.classList.toggle("bg", fromLang === "bg-BG");
  }

  const answerInput = byId<HTMLInputElement>("answerInput");
  if (answerInput) {
    answerInput.value = "";
    answerInput.removeAttribute("readonly");
    answerInput.classList.remove("correct");
    answerInput.classList.remove("wrong");
    // Placeholder adapts to shape: bilingual announces the target lang
    // ("Réponse en bulgare…"), mono uses the face label
    // ("Réponse : capitale…") since both faces share the UI locale.
    answerInput.placeholder =
      list.shape === "bilingual"
        ? (t("answer_ph", toLang) as string)
        : (t("answer_ph_mono", t(toFace?.labelKey ?? "")) as string);
    setTimeout(() => answerInput.focus(), 30);
  }
  const answerInputTts = byId<HTMLButtonElement>("answerInputTts");
  if (answerInputTts) {
    answerInputTts.hidden = true;
    answerInputTts.parentElement?.classList.remove("has-tts");
  }

  const feedback = byId("feedback");
  if (feedback) {
    feedback.className = "feedback";
    feedback.innerHTML = "";
  }

  const submitBtn = byId("submitBtn");
  if (submitBtn) submitBtn.textContent = t("btn_validate") as string;
  const dontKnowBtn = byId("dontKnowBtn");
  if (dontKnowBtn) {
    dontKnowBtn.textContent = t("btn_dont_know") as string;
    dontKnowBtn.classList.remove("hidden");
  }

  deps.setPhase("answer");
  deps.renderStats();
}
